import { createBookingAPI } from "../api/apiFactory";
import { log } from "../core/logger";
import { tprStorage } from "../core/storage";
import { dedupeBookingsBySeries, occursOnDate } from "../lib/recurrence";
import { utcDayKey, startOfUtcWeek, utcWeekDates } from "../lib/calendarDay";
import { LocationService } from "./LocationService";
import { RoomService } from "./RoomService";
import type { CreateBookingParams, IBookingAPI } from "../api/bookingApi";
import type { Booking, BookingFilters, BookingSort } from "../types/Booking";
import type { CanBookRequest, CanBookValidationResponse } from "../types/CanBookValidation";
import type { CanMoveRequest, CanMoveValidationResponse } from "../types/CanMoveValidation";
import type { PaginatedResponse } from "../types/Pagination";
import { endTimeToMinutes } from "@/utils/bookingTime";

/**
 * Service layer provides higher-level abstractions over the raw API.
 * Use this for:
 * - Client-side filtering/transformations
 * - Combining multiple API calls
 * - Business logic that doesn't belong in the API layer
 * - Caching strategies
 */
export class BookingService {
  public api: IBookingAPI;

  constructor(api?: IBookingAPI) {
    this.api = api || createBookingAPI();
  }

  // private extractServerFilters(filters?: BookingFilters): BookingFilters | undefined {
  //   if (!filters) return undefined;
  // }

  private applyClientFilters(bookings: Booking[], filters: BookingFilters): Booking[] {
    return bookings.filter((booking) => {
      if (filters.memberId && booking.memberId !== filters.memberId) return false;
      if (filters.locationId && booking.locationId !== filters.locationId) return false;
      if (filters.roomId && booking.roomId !== filters.roomId) return false;
      if (filters.type && booking.type !== filters.type) return false;

      // For date filtering, consider recurring bookings
      const isRecurring = !!(booking.recurrence || booking.recurrencePattern);
      const recurrencePattern = booking.recurrence || booking.recurrencePattern;

      if (filters.dateFrom) {
        // For recurring bookings (slot bookings), check if the recurrence extends past dateFrom
        if (isRecurring) {
          // If there's an 'until' date and it's before dateFrom, filter it out
          if (recurrencePattern?.until && recurrencePattern.until < filters.dateFrom) {
            return false;
          }
          // If there's no 'until' date (ongoing slot), it's always active - don't filter it out
        } else {
          // For non-recurring bookings, use the booking date
          if (booking.date < filters.dateFrom) return false;
        }
      }

      if (filters.dateTo) {
        // For recurring bookings (slot bookings)
        if (isRecurring) {
          // If there's no 'until' date (ongoing slot), it should NOT appear in past bookings
          if (!recurrencePattern?.until) {
            return false;
          }
          // If the recurrence has an 'until' date, check if it ended before dateTo
          // If it started after dateTo, filter it out
          if (booking.date > filters.dateTo) return false;
        } else {
          // For non-recurring bookings, use the booking date
          if (booking.date > filters.dateTo) return false;
        }
      }
      if (filters.specificDate) {
        // Recurrence-aware: a recurring booking should match whenever it OCCURS on the target
        // day, not only when its genesis date happens to equal it. occursOnDate handles both
        // one-off (genesis-date) and recurring (rrule expansion + exceptions) bookings.
        if (!occursOnDate(booking, new Date(filters.specificDate))) {
          return false;
        }
      }
      if (filters.startTimeAfter && booking.startTime < filters.startTimeAfter) {
        return false;
      }

      if (filters.endTimeBefore && booking.endTime > filters.endTimeBefore) {
        return false;
      }
      if (filters.hasBuffers !== undefined) {
        const hasBuffers = !!booking.hasBuffers;
        if (filters.hasBuffers !== hasBuffers) return false;
      }
      if (filters.hasAmenities !== undefined) {
        const hasAmenities = !!booking.bookedAmenities && booking.bookedAmenities.length > 0;
        if (filters.hasAmenities !== hasAmenities) return false;
      }
      if (filters.hasComments !== undefined) {
        const hasComments = !!booking.comments && booking.comments.length > 0;
        if (filters.hasComments !== hasComments) return false;
      }
      if (filters.isCancelled !== undefined && booking.isCancelled !== filters.isCancelled) return false;
      if (filters.isRecurring !== undefined) {
        const isRecurring = !!(booking.recurrence || booking.recurrencePattern);
        if (filters.isRecurring !== isRecurring) return false;
      }
      if (filters.isSystemBooking !== undefined && booking.isSystemBooking !== filters.isSystemBooking) return false;

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = booking.member?.lastName?.toLowerCase().includes(searchLower);
        const shortCodeMatch = booking.shortCode?.toLowerCase().includes(searchLower);
        const emailMatch = booking.member?.email?.toLowerCase().includes(searchLower);
        const businessNameMatch = booking.member?.businessName?.toLowerCase().includes(searchLower);

        if (!(nameMatch || shortCodeMatch || emailMatch || businessNameMatch)) {
          return false;
        }
      }

      return true;
    });
  }

  private sortBookings(bookings: Booking[], sort: BookingSort): Booking[] {
    const sorted = [...bookings];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "date":
          comparison = a.date.getTime() - b.date.getTime();
          break;
        case "startTime":
          comparison = a.startTime.localeCompare(b.startTime);
          break;
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  async getFilteredBookingsForLocationForMember(
    locationId: string,
    limit?: number,
    showBaseOnly?: boolean,
  ): Promise<Booking[]> {
    const storedMember = await Promise.resolve(tprStorage.get("tpruser"));
    if (!storedMember) {
      return [];
    }

    const response = await this.api.fetchSerializedBookings();
    let bookings: Booking[] = response.data;

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    log("bookingService", "getFilteredBookingsForLocationForMember", {
      bookings,
      locationId,
      storedMember,
    });

    const filtered = bookings.filter(
      (booking) => booking.locationId === locationId && booking.memberId === storedMember.id,
    );
    return limit ? filtered.slice(0, limit) : filtered;
  }

  // todo: this would get the bookings based on the member's token/session
  async getAllBookingsForMember(
    memberId: string,
    limit?: number,
    showBaseOnly?: boolean,
    filters?: BookingFilters,
    sort?: BookingSort,
  ): Promise<Booking[]> {
    let bookings: Booking[] = (await this.api.fetchSerializedBookings()).data;

    bookings = bookings.filter((booking) => booking.memberId === memberId);

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    if (filters) {
      bookings = this.applyClientFilters(bookings, filters);
    }

    if (sort) {
      bookings = this.sortBookings(bookings, sort);
    }

    return limit ? bookings.slice(0, limit) : bookings;
  }

  async getBookingsForLocationAndDate(
    locationSlug: string,
    date: Date,
    showBaseOnly: boolean = false,
  ): Promise<Booking[]> {
    // Use the same date-filtered API as week view to ensure recurring bookings are included
    // The backend only returns recurring bookings when dateFrom/dateTo are provided
    const response = await this.api.fetchBookingsForDay(date, locationSlug);
    let bookings = response.data;
    bookings = bookings.filter((booking) => occursOnDate(booking, date));

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    console.log("BookingService.getBookingsForLocationAndDate", { locationSlug, date, bookings });

    return bookings;
  }

  // pm/admin only
  async getBookingsForLocation(
    locationId: string,
    filters?: BookingFilters,
    sort?: BookingSort,
    showBaseOnly: boolean = false,
  ): Promise<Booking[]> {
    let bookings = (await this.api.fetchSerializedBookings()).data.filter(
      (booking) => booking.locationId === locationId,
    );

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    if (filters) {
      bookings = this.applyClientFilters(bookings, filters);
    }

    if (sort) {
      bookings = this.sortBookings(bookings, sort);
    }

    return bookings;
  }

  // todo: this can only get bookings that the member has access to
  async getBookingById(bookingId: string): Promise<Booking | null> {
    log("BookingService.getBookingById", { bookingId });
    const booking = await this.api.fetchBookingById(bookingId);

    if (!booking) {
      return null;
    }

    log("BookingService.getBookingById", "Enriching booking", { booking });

    // Enrich booking with location and room details
    const locationService = new LocationService();
    const roomService = new RoomService();

    // Fetch location details including address
    const location = await locationService.getLocationById(booking.locationId);
    if (location) {
      booking.location = location;
    }

    // Fetch room details including swatch
    const room = await roomService.getRoomById(booking.roomId);
    if (room) {
      booking.room = room;
    }

    return booking;
  }

  // admin only
  async getAllBookings(
    showBaseOnly: boolean = false,
    filters?: BookingFilters,
    sort?: BookingSort,
    skip?: number,
    limit?: number,
  ): Promise<PaginatedResponse<Booking>> {
    // Server-side filters: searchTerm, skip, limit, locationId, isCancelled, memberId,
    // organisationId, type, isRecurring, isSystemBooking, specificDate. Anything not in this
    // list runs client-side after pagination, which means it can silently drop a page to zero
    // rows when the matching records live further into the dataset. organisationId expands to
    // all org members on the server (elevated callers only). specificDate is forwarded as a
    // recurrence-aware day window so the day filter narrows BEFORE pagination; we still keep it
    // in the client-side pass below (occursOnDate) to refine the coarse server window to the
    // exact day (weekday/interval/exceptions the Mongo query can't expand).
    const response = await this.api.fetchSerializedBookings(
      filters?.searchTerm,
      skip,
      limit,
      filters?.locationId,
      filters?.isCancelled,
      filters?.memberId,
      filters?.organisationId,
      filters?.type,
      filters?.isRecurring,
      filters?.isSystemBooking,
      filters?.specificDate,
      filters?.dateFrom,
      filters?.dateTo,
      filters?.swatch,
    );
    let bookings = response.data;

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    // Apply remaining client-side filters (excluding those handled server-side)
    if (filters) {
      const clientFilters = { ...filters };
      delete clientFilters.searchTerm;
      delete clientFilters.locationId;
      delete clientFilters.isCancelled;
      delete clientFilters.memberId;
      delete clientFilters.organisationId;
      delete clientFilters.type;
      delete clientFilters.isRecurring;
      delete clientFilters.isSystemBooking;
      // dateFrom/dateTo/swatch are now server-handled (recurrence-aware + colour, pre-pagination).
      // Leaving them in clientFilters would let applyClientFilters re-run its date/colour logic on
      // the already-filtered page and wrongly drop rows — so strip them here (mirrors specificDate's
      // intent, except specificDate is deliberately kept to refine the coarse server day-window).
      delete clientFilters.dateFrom;
      delete clientFilters.dateTo;
      delete clientFilters.swatch;
      if (Object.keys(clientFilters).length > 0) {
        bookings = this.applyClientFilters(bookings, clientFilters);
      }
    }

    if (sort) {
      bookings = this.sortBookings(bookings, sort);
    }

    return {
      ...response,
      data: bookings,
    };
  }

  async getBookingsForWeek(
    date: Date,
    locationId: string,
    showBaseOnly: boolean = false,
    filters?: BookingFilters,
    sort?: BookingSort,
  ) {
    // Anchor to the Monday→Sunday UTC week the grid actually renders (BrickWeekSchedule draws
    // `getWeekDates(date)` = utcWeekDates(startOfUtcWeek(date))). Fetching/filtering `[date, date+7)`
    // instead — anchored on the selected day rather than the week's Monday — dropped every rendered
    // day BEFORE `date` (e.g. yesterday when the default `date` is today): non-recurring bookings on
    // those days vanished from the grid while recurring ones survived by recurring into the later
    // window. Stepping was also LOCAL (setDate) against UTC-anchored occursOnDate. Both are fixed by
    // deriving the week from startOfUtcWeek and iterating its 7 UTC days.
    const weekStart = startOfUtcWeek(date);
    const weekDays = utcWeekDates(weekStart);

    // Backend returns bookings with recurrence-aware date filtering for the requested week.
    const response = await this.api.fetchBookingsForWeek(weekStart, locationId);
    let bookings = response.data;

    // Keep only bookings that actually occur on a rendered day of the week — base bookings may carry
    // a genesis date outside the week whose recurrence still lands inside it.
    bookings = bookings.filter((booking) => weekDays.some((day) => occursOnDate(booking, day)));

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    if (filters) {
      bookings = this.applyClientFilters(bookings, filters);
    }

    if (sort) {
      bookings = this.sortBookings(bookings, sort);
    }

    return bookings;
  }

  async getBookingsForMonth(
    date: Date,
    locationId: string,
    showBaseOnly: boolean = false,
    filters?: BookingFilters,
    sort?: BookingSort,
  ) {
    // Backend now returns bookings with recurrence-aware date filtering
    const response = await this.api.fetchBookingsForMonth(date, locationId);
    let bookings = response.data;

    // Filter to only bookings that actually occur within the month range
    // This is necessary because base bookings might have dates outside the range
    // but their recurrence patterns extend into it
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 30);

    bookings = bookings.filter((booking) => {
      // Check if booking occurs on any day in the month range
      const currentDate = new Date(date);
      while (currentDate < endDate) {
        if (occursOnDate(booking, currentDate)) {
          return true;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return false;
    });

    if (showBaseOnly) {
      // For recurring bookings:
      // - Keep all instance bookings (those with recurrencePattern)
      // - Keep base bookings (those with recurrence) - they represent the pattern
      // The calendar component will handle displaying them correctly by checking occursOnDate
      // This is simpler than trying to filter base bookings here because:
      // 1. A base booking represents multiple dates through its recurrence pattern
      // 2. We've already filtered bookings to those that occur in the date range
      // 3. The calendar will use occursOnDate() to determine which specific dates to display them on
      // No filtering needed - we keep all bookings that passed occursOnDate filter
    }

    if (filters) {
      bookings = this.applyClientFilters(bookings, filters);
    }

    if (sort) {
      bookings = this.sortBookings(bookings, sort);
    }

    return bookings;
  }

  async getBookingsForRoomByDate(roomId: string, date: Date): Promise<Booking[]> {
    const bookings = (await this.api.fetchSerializedBookings()).data;
    return bookings.filter((booking) => booking.roomId === roomId && occursOnDate(booking, date));
  }

  async getWeeklyBookingsForRoom(
    roomId: string,
    date: Date,
    numberOfWeeks: number,
    locationId?: string,
  ): Promise<Booking[]> {
    return this.api.fetchWeeklyBookingsForRoom(roomId, date, numberOfWeeks, locationId);
  }

  /**
   * Check availability for a recurring booking across multiple occurrences
   * @param roomId - The room to book
   * @param startDate - The first occurrence date
   * @param startTime - Time in HHMM format
   * @param durationMinutes - Duration in minutes
   * @param occurrenceCount - Number of occurrences to check (e.g., 8 weeks)
   * @param memberId - The member making the booking
   * @param frequency - Recurrence frequency: "weekly" or "monthly"
   * @param interval - Interval between occurrences (e.g., 2 for fortnightly)
   * @returns Object with conflicts array containing dates where conflicts exist
   */
  async checkRecurringAvailability(
    roomId: string,
    startDate: Date,
    startTime: string,
    durationMinutes: number,
    occurrenceCount: number,
    memberId: string,
    frequency: "weekly" | "monthly" = "weekly",
    interval: number = 1,
    /**
     * The booking we're checking availability FOR. When set, that booking is removed from
     * the room's existing-booking set before overlap is tested — without this the Manage
     * Series panel reports every occurrence as "clashes with itself" because the recurring
     * booking is by definition present on every one of its own occurrence dates (R7 was
     * designed for different bookings, not the same one).
     */
    excludeBookingId?: string,
    /**
     * Pre-fetched candidate bookings to test against, instead of an unscoped fetch. Callers
     * checking a single date MUST pass a date-scoped set (see checkSingleDateAvailability) so
     * the verdict matches the server's authoritative getBookingsAffectingRoomOnDate. Without
     * this the default fetch is capped at the server's 200-row page and can silently drop a
     * genuine conflict — the cause of the "free in the preview, not free on reinstate" bug.
     */
    candidateBookings?: Booking[],
  ): Promise<{
    conflicts: Date[];
    availableCount: number;
    /**
     * Per-occurrence clash details keyed by ISO date (YYYY-MM-DD). Each entry lists every
     * booking that conflicts with the proposed occurrence on that day. Empty list = available.
     * Drives the per-row "Clashes with 11:00 Gregory" wording in BookingOccurrenceList.
     */
    clashesByDateIso: Map<
      string,
      Array<{
        bookingId: string;
        memberLastName: string;
        startTime: string;
        /** Core-time overlap (R7, always blocks) vs buffer-only overlap against a different
         *  member (R5) — see OccurrenceClash in BookingOccurrenceList.tsx. */
        conflictType: "core_overlap" | "buffer_overlap";
      }>
    >;
  }> {
    const BUFFER_MINUTES = 15;
    const conflicts: Date[] = [];
    const clashesByDateIso = new Map<
      string,
      Array<{
        bookingId: string;
        memberLastName: string;
        startTime: string;
        conflictType: "core_overlap" | "buffer_overlap";
      }>
    >();

    const formatHHmmToColon = (t: string): string => {
      const padded = t.padStart(4, "0");
      return `${padded.slice(0, 2)}:${padded.slice(2)}`;
    };
    // Day-identity, not a time-of-day instant — the booking's `date` is UTC-midnight of the
    // centre's day, so this must key off the UTC calendar day (utcDayKey) rather than the
    // viewer's browser-local day, or a viewer west of UTC reads/writes the wrong day's clashes.
    const toIsoDate = (d: Date): string => utcDayKey(d);

    // Helper to convert time to minutes
    const timeToMinutes = (time: string): number => {
      const cleanTime = time.replace(":", "");
      const hours = parseInt(cleanTime.substring(0, 2), 10);
      const minutes = parseInt(cleanTime.substring(2, 4), 10);
      return hours * 60 + minutes;
    };

    // Helper to get the nth occurrence of a weekday in a month (day-identity math — all UTC).
    const getNthWeekdayOfMonth = (year: number, month: number, dayOfWeek: number, n: number): Date | null => {
      const firstOfMonth = new Date(Date.UTC(year, month, 1));
      const firstDayOfWeek = firstOfMonth.getUTCDay();

      // Calculate the date of the first occurrence of the target weekday
      let firstOccurrence = 1 + ((dayOfWeek - firstDayOfWeek + 7) % 7);

      // Calculate the date of the nth occurrence
      const targetDate = firstOccurrence + (n - 1) * 7;

      // Check if this date exists in the month
      const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      if (targetDate > lastDayOfMonth) {
        return null; // nth occurrence doesn't exist in this month
      }

      return new Date(Date.UTC(year, month, targetDate));
    };

    // Calculate proposed booking times
    const proposedStartMinutes = timeToMinutes(startTime);
    const proposedEndMinutes = proposedStartMinutes + durationMinutes;
    const proposedBufferStart = proposedStartMinutes - BUFFER_MINUTES;
    const proposedBufferEnd = proposedEndMinutes + BUFFER_MINUTES;

    // Generate occurrence dates based on frequency
    const occurrenceDates: Date[] = [];

    if (frequency === "monthly") {
      // For monthly recurrence, use "same weekday" pattern (e.g., 3rd Monday)
      const dayOfWeek = startDate.getUTCDay();
      const nthWeekday = Math.ceil(startDate.getUTCDate() / 7); // 1st, 2nd, 3rd, 4th, or 5th occurrence

      let currentMonth = startDate.getUTCMonth();
      let currentYear = startDate.getUTCFullYear();

      for (let i = 0; i < occurrenceCount; i++) {
        if (i === 0) {
          // First occurrence is the start date itself
          occurrenceDates.push(new Date(startDate));
        } else {
          // Move forward by interval months
          currentMonth += interval;
          if (currentMonth > 11) {
            currentYear += Math.floor(currentMonth / 12);
            currentMonth = currentMonth % 12;
          }

          const nthWeekdayDate = getNthWeekdayOfMonth(currentYear, currentMonth, dayOfWeek, nthWeekday);
          if (nthWeekdayDate) {
            occurrenceDates.push(nthWeekdayDate);
          }
          // If nth weekday doesn't exist in that month (e.g., 5th Monday), skip it
        }
      }
    } else {
      // Weekly recurrence (including fortnightly with interval=2). Step in UTC calendar days so
      // the occurrence stays on the centre's day regardless of the viewer's browser timezone.
      for (let i = 0; i < occurrenceCount; i++) {
        const occurrenceDate = new Date(
          Date.UTC(
            startDate.getUTCFullYear(),
            startDate.getUTCMonth(),
            startDate.getUTCDate() + i * 7 * interval,
          ),
        );
        occurrenceDates.push(occurrenceDate);
      }
    }

    // Fetch all bookings for this room. Prefer a caller-supplied, date-scoped candidate set
    // (single-date checks pass one); fall back to the unscoped fetch for multi-occurrence use.
    const allBookings = candidateBookings ?? (await this.api.fetchSerializedBookings()).data;
    const roomBookings = allBookings.filter(
      (b) => b.roomId === roomId && !b.isCancelled && (excludeBookingId ? b.id !== excludeBookingId : true),
    );

    // Check each occurrence date for conflicts
    for (const occurrenceDate of occurrenceDates) {
      const bookingsOnDate = roomBookings.filter((booking) => occursOnDate(booking, occurrenceDate));

      const clashList: Array<{
        bookingId: string;
        memberLastName: string;
        startTime: string;
        conflictType: "core_overlap" | "buffer_overlap";
      }> = [];
      for (const booking of bookingsOnDate) {
        const bookingStartMinutes = timeToMinutes(booking.startTime);
        const bookingEndMinutes = endTimeToMinutes(booking.endTime, booking.startTime);
        const hasBuffers = booking.hasBuffers ?? true;
        const isSameMember = booking.memberId === memberId;

        const bookingBufferStart = hasBuffers ? bookingStartMinutes - BUFFER_MINUTES : bookingStartMinutes;
        const bookingBufferEnd = hasBuffers ? bookingEndMinutes + BUFFER_MINUTES : bookingEndMinutes;

        // Check for core time overlap (R7 — always a clash, even for same member)
        const isCoreOverlap = proposedStartMinutes < bookingEndMinutes && proposedEndMinutes > bookingStartMinutes;
        if (isCoreOverlap) {
          clashList.push({
            bookingId: booking.id,
            memberLastName: booking.member?.lastName ?? booking.title ?? "Unknown",
            startTime: formatHHmmToColon(booking.startTime),
            conflictType: "core_overlap",
          });
          continue;
        }

        // For same member: Allow buffer overlap (R6 — adjacent bookings are fine)
        if (isSameMember) {
          continue;
        }

        // For different members: Check for buffer overlap (R5 — different-member buffers can't overlap)
        const isBufferOverlap =
          proposedBufferStart < bookingBufferEnd && proposedBufferEnd > bookingBufferStart;
        if (isBufferOverlap) {
          clashList.push({
            bookingId: booking.id,
            memberLastName: booking.member?.lastName ?? booking.title ?? "Unknown",
            startTime: formatHHmmToColon(booking.startTime),
            conflictType: "buffer_overlap",
          });
        }
      }

      if (clashList.length > 0) {
        conflicts.push(occurrenceDate);
        // Sort by time so wording reads naturally ("10:00, 10:30, 11:00")
        clashList.sort((a, b) => a.startTime.localeCompare(b.startTime));
        clashesByDateIso.set(toIsoDate(occurrenceDate), clashList);
      }
    }

    return {
      conflicts,
      availableCount: occurrenceCount - conflicts.length,
      clashesByDateIso,
    };
  }

  // admin/pm only
  async getBookingsForMember(
    memberId: string,
    limit?: number,
    showBaseOnly?: boolean,
    filters?: BookingFilters,
    sort?: BookingSort,
  ): Promise<Booking[]> {
    // Pass isCancelled and memberId to API so the server-side query is scoped to this member
    const bookings = (await this.api.fetchSerializedBookings(undefined, undefined, undefined, undefined, filters?.isCancelled, memberId)).data;

    let filtered = limit ? bookings.slice(0, limit) : bookings;

    if (showBaseOnly) {
      // Only show the base booking for recurrence base/instance relationships.
      // For bookings with a parentBookingId, we want to show only the base, not its instances
      const seenBaseIds = new Set<string>();
      const instanceBookingIds = new Set<string>();

      // First pass: identify all instances
      filtered.forEach((booking) => {
        if (booking.parentBookingId) {
          instanceBookingIds.add(booking.id);
        }
      });

      // Second pass: filter out instances, keep base bookings
      filtered = filtered.filter((booking) => {
        // If this booking links back to a base, exclude it (it's an instance)
        if (booking.parentBookingId) {
          // Track that we've seen this base
          if (!seenBaseIds.has(booking.parentBookingId)) {
            seenBaseIds.add(booking.parentBookingId);
          }
          return false;
        }
        // Keep all bookings that are not instances
        return true;
      });
    }

    if (filters) {
      filtered = this.applyClientFilters(filtered, filters);
    }

    if (sort) {
      filtered = this.sortBookings(filtered, sort);
    }

    return filtered;
  }

  /**
   * Check if a member has any conflicting bookings in other rooms during the proposed time
   * @param memberId - The member to check
   * @param date - The date to check
   * @param startTime - Start time in HHMM format
   * @param endTime - End time in HHMM format
   * @param excludeRoomId - Room ID to exclude from check (the room being booked)
   * @returns Object with conflict status and details
   */
  async checkMemberAvailabilityAcrossRooms(
    memberId: string,
    date: Date,
    startTime: string,
    endTime: string,
    excludeRoomId?: string,
  ): Promise<{ hasConflict: boolean; conflictingBookings: Booking[] }> {
    // Helper to convert time to minutes
    const timeToMinutes = (time: string): number => {
      const cleanTime = time.replace(":", "");
      const hours = parseInt(cleanTime.substring(0, 2), 10);
      const minutes = parseInt(cleanTime.substring(2, 4), 10);
      return hours * 60 + minutes;
    };

    const proposedStartMinutes = timeToMinutes(startTime);
    const proposedEndMinutes = endTimeToMinutes(endTime, startTime);

    // Fetch all bookings for this member
    const allBookings = (await this.api.fetchSerializedBookings()).data;
    const memberBookings = allBookings.filter(
      (b) => b.memberId === memberId && !b.isCancelled && (excludeRoomId ? b.roomId !== excludeRoomId : true),
    );

    // Filter bookings that occur on the same date
    const bookingsOnDate = memberBookings.filter((booking) => occursOnDate(booking, date));

    const conflictingBookings: Booking[] = [];

    // Check each booking for time overlap
    for (const booking of bookingsOnDate) {
      const bookingStartMinutes = timeToMinutes(booking.startTime);
      const bookingEndMinutes = endTimeToMinutes(booking.endTime, booking.startTime);

      // Check if there's any time overlap
      if (proposedStartMinutes < bookingEndMinutes && proposedEndMinutes > bookingStartMinutes) {
        conflictingBookings.push(booking);
      }
    }

    return {
      hasConflict: conflictingBookings.length > 0,
      conflictingBookings,
    };
  }

  /**
   * Create a new booking
   * @param params - Booking parameters
   * @returns The created booking
   */
  async createNewBooking(params: CreateBookingParams): Promise<Booking> {
    log("bookingService", "createNewBooking", { params });

    try {
      const booking = await this.api.addNewBooking(params);
      log("bookingService", "createNewBooking", "Booking created successfully:", booking);

      // Invalidate React Query caches so the calendar (and every other view
      // bound to booking data) reflects the new booking immediately. Without
      // this the UI only picks up the change on the next 60s poll — the exact
      // "new bookings take up to a minute to appear" bug. Matches the pattern
      // used by `addNotesToBooking` below.
      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();

      return booking;
    } catch (error) {
      log("bookingService", "createNewBooking", "Failed to create booking:", error);
      throw error;
    }
  }

  async addNotesToBooking(bookingId: string, notes: string): Promise<Booking | null> {
    const updatedBooking = await this.api.updateNotesOnBooking(bookingId, notes);

    // Invalidate React Query cache for all booking-related queries
    const { invalidateBookingQueries } = await import("@/lib/queryClient");
    await invalidateBookingQueries();

    return updatedBooking;
  }

  async getBookingsForMemberOnDate(memberId: string, date: Date): Promise<Booking[]> {
    const bookings = (await this.api.fetchSerializedBookings()).data;
    // Mirror the Your Bookings list exactly so the dashboard "you have N bookings today" count
    // can never diverge from it: (1) scope to the member and drop cancelled bookings —
    // fetchSerializedBookings() returns cancelled ones; (2) dedupe recurring base/instance
    // documents to one row per series; (3) keep those that occur on `date`. See SimpleBookingsList.
    const mine = bookings.filter((booking) => booking.memberId === memberId && !booking.isCancelled);
    return dedupeBookingsBySeries(mine).filter((booking) => occursOnDate(booking, date));
  }

  /**
   * Get booking counts grouped by location for the authenticated member
   * Uses MongoDB aggregation on the backend for efficient counting
   * @returns Map of locationId to booking count
   */
  async getBookingCountsByLocation(): Promise<Record<string, number>> {
    return this.api.fetchBookingCountsByLocation();
  }

  /**
   * Check if a booking can be made with comprehensive validation
   * @param params - Booking parameters to validate
   * @returns Validation response with detailed checks
   */
  async checkCanBook(params: CanBookRequest): Promise<CanBookValidationResponse> {
    log("bookingService", "checkCanBook", { params });

    try {
      const response = await this.api.checkCanBook(params);
      log("bookingService", "checkCanBook", "Validation complete:", { canBook: response.canBook });
      return response;
    } catch (error) {
      log("bookingService", "checkCanBook", "Failed to check booking:", error);
      throw error;
    }
  }

  /**
   * Check if an existing booking can be moved to a new date/time
   * @param params - Move parameters including booking ID and new date/time
   * @returns Validation response with detailed checks and any conflicting dates
   */
  async checkCanMove(params: CanMoveRequest): Promise<CanMoveValidationResponse> {
    log("bookingService", "checkCanMove", { params });

    try {
      const response = await this.api.checkCanMove(params);
      log("bookingService", "checkCanMove", "Validation complete:", {
        canMove: response.canMove,
        conflictCount: response.conflictingDates?.length || 0,
      });
      return response;
    } catch (error) {
      log("bookingService", "checkCanMove", "Failed to check if booking can be moved:", error);
      throw error;
    }
  }

  /**
   * Update booking date and time
   * @param bookingId - ID of the booking to update
   * @param newDate - New date in ISO format (YYYY-MM-DD)
   * @param newStartTime - New start time in HHmm format
   * @param newEndTime - New end time in HHmm format
   * @returns Updated booking
   */
  async updateBookingDateTime(bookingId: string, newDate: string, newStartTime: string, newEndTime: string, waive?: boolean): Promise<Booking> {
    log("bookingService", "updateBookingDateTime", { bookingId, newDate, newStartTime, newEndTime, waive });

    try {
      const updatedBooking = await this.api.updateBookingDateTime(bookingId, newDate, newStartTime, newEndTime, waive);
      log("bookingService", "updateBookingDateTime", "Successfully updated booking", { bookingId });

      // Invalidate all booking-related queries to trigger a refresh
      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();

      return updatedBooking;
    } catch (error) {
      log("bookingService", "updateBookingDateTime", "Failed to update booking:", error);
      throw error;
    }
  }

  /**
   * Update booking room
   * @param bookingId - ID of the booking to update
   * @param newRoomId - New room ID
   * @returns Updated booking
   */
  async updateBookingRoom(bookingId: string, newRoomId: string): Promise<Booking> {
    log("bookingService", "updateBookingRoom", { bookingId, newRoomId });

    try {
      const updatedBooking = await this.api.updateBookingRoom(bookingId, newRoomId);
      log("bookingService", "updateBookingRoom", "Successfully updated booking room", { bookingId });

      // Invalidate all booking-related queries to trigger a refresh
      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();

      return updatedBooking;
    } catch (error) {
      log("bookingService", "updateBookingRoom", "Failed to update booking room:", error);
      throw error;
    }
  }

  async setRecurrenceException(
    bookingId: string,
    date: string,
    action: "add" | "remove",
    waive?: boolean,
  ): Promise<Booking> {
    log("bookingService", "setRecurrenceException", { bookingId, date, action, waive });
    const result = await this.api.setRecurrenceException(bookingId, date, action, waive);
    const { invalidateBookingQueries } = await import("@/lib/queryClient");
    await invalidateBookingQueries();
    return result.booking;
  }

  /**
   * Check whether a single date's slot in a given room is free for a given member. Wraps
   * `checkRecurringAvailability` with `occurrenceCount: 1` so the same R3/R5/R6/R7 logic is
   * reused — including same-member buffer-overlap allowances. Returns the clash list for the
   * date (empty when the slot is free). Used to gate reinstating a previously-excluded
   * recurrence occurrence.
   *
   * Fetches bookings scoped to `date` (recurrence-aware, narrowed server-side BEFORE
   * pagination). This is essential: an unscoped fetch is capped at the server's 200-row
   * default and can drop the real conflict, so the reinstate preview would report "free"
   * while the server's authoritative getBookingsAffectingRoomOnDate reports "not free".
   */
  async checkSingleDateAvailability(
    roomId: string,
    date: Date,
    startTime: string,
    durationMinutes: number,
    memberId: string,
    /**
     * The booking's location. MUST be passed: without it the server's role gate restricts a
     * non-elevated member to their OWN bookings, so a slot re-booked by ANOTHER member while it
     * was excluded is invisible to this preview — the preview then says "free" while the
     * authoritative reinstate (getBookingsAffectingRoomOnDate, all bookings in the room) says
     * "not free". Passing locationId puts the request on the calendar-view path (every booking at
     * the location), matching the server's check.
     */
    locationId?: string,
  ): Promise<
    Array<{
      bookingId: string;
      memberLastName: string;
      startTime: string;
      conflictType: "core_overlap" | "buffer_overlap";
    }>
  > {
    // specificDate is the 11th positional arg on fetchSerializedBookings; the server expands
    // it to a recurrence-aware [startOfDay, endOfDay] window applied before pagination.
    const scopedBookings = (
      await this.api.fetchSerializedBookings(
        undefined, // searchTerm
        undefined, // skip
        undefined, // limit
        locationId, // locationId — calendar-view path so OTHER members' bookings are visible
        undefined, // isCancelled
        undefined, // memberId
        undefined, // organisationId
        undefined, // type
        undefined, // isRecurring
        undefined, // isSystemBooking
        date, // specificDate
      )
    ).data;

    const result = await this.checkRecurringAvailability(
      roomId,
      date,
      startTime,
      durationMinutes,
      1,
      memberId,
      "weekly",
      1,
      undefined, // excludeBookingId — the date is still excluded, so the series won't self-clash
      scopedBookings,
    );
    // checkRecurringAvailability keys by YYYY-MM-DD; there's exactly one occurrence here.
    const iso = utcDayKey(date);
    return result.clashesByDateIso.get(iso) ?? [];
  }

  /**
   * Cancel a booking
   * @param bookingId - ID of the booking to cancel
   * @param opts - Optional. Pass `{ waive: true }` to request the server waive the C1 charge
   *   (elevated users only; the server ignores waive from non-elevated callers).
   * @returns Cancelled booking
   */
  async cancelBooking(bookingId: string, opts?: { waive?: boolean; waiveNote?: string }): Promise<Booking> {
    log("bookingService", "cancelBooking", { bookingId });

    try {
      const cancelledBooking = await this.api.cancelBooking(bookingId, opts);
      log("bookingService", "cancelBooking", "Successfully cancelled booking", { bookingId });

      // Invalidate all booking-related queries to trigger a refresh
      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();

      return cancelledBooking;
    } catch (error) {
      log("bookingService", "cancelBooking", "Failed to cancel booking:", error);
      throw error;
    }
  }

  /**
   * Cancel all occurrences of a recurring booking from a given date onwards.
   * @param bookingId - ID of the booking series to partially cancel
   * @param fromDate - ISO date string (YYYY-MM-DD); occurrences on/after this date are cancelled
   * @param opts - Optional. Pass `{ waive: true }` to request the server waive the C1 charge
   *   (elevated users only; the server ignores waive from non-elevated callers).
   * @returns Result summary including how many occurrences were cancelled
   */
  async cancelBookingFromDate(
    bookingId: string,
    fromDate: string,
    opts?: { waive?: boolean; waiveNote?: string },
  ): Promise<{ success: boolean; cancelledCount: number; wasChargedOnCancel: boolean; message: string }> {
    log("bookingService", "cancelBookingFromDate", { bookingId, fromDate });

    try {
      const result = await this.api.cancelBookingFromDate(bookingId, fromDate, opts);
      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();
      return result;
    } catch (error) {
      log("bookingService", "cancelBookingFromDate", "Failed:", error);
      throw error;
    }
  }

  /**
   * Upgrade a flex (recurring weekly) booking to a slot booking.
   * The server enforces the eligibility rules and returns a discriminated `reason`
   * on failure which the API layer rethrows as the Error message.
   * @param bookingId - ID of the booking to upgrade
   * @returns Upgraded booking
   */
  async upgradeFlexToSlot(bookingId: string, slotStartOverride?: string): Promise<Booking> {
    log("bookingService", "upgradeFlexToSlot", { bookingId, slotStartOverride });

    try {
      const upgraded = await this.api.upgradeFlexToSlot(bookingId, slotStartOverride);
      log("bookingService", "upgradeFlexToSlot", "Successfully upgraded to slot", { bookingId });

      const { invalidateBookingQueries } = await import("@/lib/queryClient");
      await invalidateBookingQueries();

      return upgraded;
    } catch (error) {
      log("bookingService", "upgradeFlexToSlot", "Failed to upgrade:", error);
      throw error;
    }
  }

  async getUpgradeToSlotPreview(bookingId: string) {
    return this.api.getUpgradeToSlotPreview(bookingId);
  }

  /** The up-to-52 most-recent PAST occurrences of a recurring booking, classified held/holiday/
   *  cancelled with a per-occurrence waived flag. Backs the "Past Bookings" tab. */
  async getOccurrenceHistory(bookingId: string) {
    return this.api.getOccurrenceHistory(bookingId);
  }
}
